/**
 * Supervision des tickets — la vente elle-même se fait au POS mobile.
 *
 * On regarde ce qui est ouvert, ce qui a été encaissé, et on peut annuler un
 * ticket. Le détail montre qui a fait quoi, pour qui, pour combien.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Card, CardContent, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  CircularProgress, Alert, Tabs, Tab, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, InputAdornment, Divider,
} from '@mui/material';
import {
  Search, Visibility, Cancel, Receipt, Refresh, Person, LocalCafe,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { ticketStudioAPI, extraireListe } from '../../../services/studioAPI';

const htg = (valeur) => `${Number(valeur || 0).toLocaleString()} HTG`;

const isoLocal = (date) => {
  const mois = String(date.getMonth() + 1).padStart(2, '0');
  const jour = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${mois}-${jour}`;
};

const premierDuMois = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const dateHeure = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      });
};

const STATUTS = {
  ouvert: { label: 'Ouvert', couleur: 'info' },
  paye: { label: 'Payé', couleur: 'success' },
  annule: { label: 'Annulé', couleur: 'default' },
};

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [onglet, setOnglet] = useState(0);

  const [dateDebut, setDateDebut] = useState(isoLocal(premierDuMois()));
  const [dateFin, setDateFin] = useState(isoLocal(new Date()));
  const [filtreStatut, setFiltreStatut] = useState('');
  const [recherche, setRecherche] = useState('');

  const [detail, setDetail] = useState(null);

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      setErreur(null);
      const params = { date_debut: dateDebut, date_fin: dateFin };
      if (filtreStatut) params.statut = filtreStatut;
      const reponse = await ticketStudioAPI.getAll(params);
      setTickets(extraireListe(reponse));
    } catch (error) {
      const message = error.response?.data?.detail || error.message
        || 'Erreur lors du chargement des tickets';
      setErreur(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [dateDebut, dateFin, filtreStatut]);

  useEffect(() => { charger(); }, [charger]);

  const parOnglet = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    const correspond = (ticket) => !terme
      || `${ticket.numero} ${ticket.client_nom || ''}`.toLowerCase().includes(terme);

    const filtres = tickets.filter(correspond);
    return onglet === 0
      ? filtres.filter((ticket) => ticket.statut === 'ouvert')
      : filtres.filter((ticket) => ticket.statut !== 'ouvert');
  }, [tickets, recherche, onglet]);

  const ouverts = tickets.filter((ticket) => ticket.statut === 'ouvert');
  const payes = tickets.filter((ticket) => ticket.statut === 'paye');
  const encaisse = payes.reduce((somme, ticket) => somme + Number(ticket.montant_total || 0), 0);
  const panierMoyen = payes.length ? Math.round(encaisse / payes.length) : 0;

  const annuler = async (ticket) => {
    const message = ticket.statut === 'paye'
      ? `Annuler le ticket payé ${ticket.numero} ? La visite du client sera décomptée.`
      : `Annuler le ticket ${ticket.numero} ?`;
    if (!window.confirm(message)) return;

    try {
      await ticketStudioAPI.annuler(ticket.id);
      toast.success('Ticket annulé');
      setDetail(null);
      charger();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'annulation");
    }
  };

  if (loading && tickets.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Tickets</Typography>
          <Typography variant="body2" color="text.secondary">
            Supervision — la prise de commande se fait à la caisse mobile
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={charger}>Actualiser</Button>
      </Box>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">En cours</Typography>
            <Typography variant="h4" fontWeight="bold" color="info.main">
              {ouverts.length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Payés</Typography>
            <Typography variant="h4" fontWeight="bold">{payes.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Encaissé</Typography>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {htg(encaisse)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Panier moyen</Typography>
            <Typography variant="h4" fontWeight="bold">{htg(panierMoyen)}</Typography>
          </CardContent>
        </Card>
      </Box>

      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          size="small" type="date" label="Du" value={dateDebut}
          onChange={(e) => setDateDebut(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small" type="date" label="Au" value={dateFin}
          onChange={(e) => setDateFin(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small" select label="Statut" value={filtreStatut}
          onChange={(e) => setFiltreStatut(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Tous</MenuItem>
          <MenuItem value="ouvert">Ouvert</MenuItem>
          <MenuItem value="paye">Payé</MenuItem>
          <MenuItem value="annule">Annulé</MenuItem>
        </TextField>
        <TextField
          size="small" placeholder="Numéro ou client" value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          sx={{ flex: '1 1 240px' }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
          }}
        />
      </Box>

      <Tabs value={onglet} onChange={(e, valeur) => setOnglet(valeur)} sx={{ mb: 2 }}>
        <Tab label={`En cours (${ouverts.length})`} />
        <Tab label="Historique" />
      </Tabs>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ticket</TableCell>
              <TableCell>Ouvert le</TableCell>
              <TableCell>Client</TableCell>
              <TableCell align="center">Articles</TableCell>
              <TableCell align="center">Statut</TableCell>
              <TableCell align="right">Montant</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parOnglet.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Receipt sx={{ fontSize: 48, color: 'text.disabled' }} />
                  <Typography color="text.secondary" mt={1}>
                    {onglet === 0
                      ? 'Aucun ticket en cours'
                      : 'Aucun ticket sur cette période'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : parOnglet.map((ticket) => {
              const statut = STATUTS[ticket.statut] || STATUTS.ouvert;
              return (
                <TableRow key={ticket.id} hover>
                  <TableCell><Typography fontWeight={600}>{ticket.numero}</Typography></TableCell>
                  <TableCell>{dateHeure(ticket.date_ticket)}</TableCell>
                  <TableCell>
                    {ticket.client_nom || (
                      <Typography color="text.disabled" fontStyle="italic">
                        Sans fiche client
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={ticket.nombre_items ?? 0} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={statut.label} color={statut.couleur} />
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600}>{htg(ticket.montant_total)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Voir le détail">
                      <IconButton size="small" onClick={() => setDetail(ticket)}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    {ticket.statut !== 'annule' && (
                      <Tooltip title="Annuler">
                        <IconButton size="small" color="error" onClick={() => annuler(ticket)}>
                          <Cancel />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Détail */}
      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {detail?.numero}
          <Typography variant="body2" color="text.secondary">
            {detail?.client_nom || 'Sans fiche client'} · {dateHeure(detail?.date_ticket)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Article</TableCell>
                <TableCell>Réalisé par</TableCell>
                <TableCell align="center">Qté</TableCell>
                <TableCell align="right">Montant</TableCell>
                <TableCell align="right">Part</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail?.lignes?.map((ligne) => (
                <TableRow key={ligne.id}>
                  <TableCell>
                    {ligne.type_item === 'article' && (
                      <LocalCafe fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    )}
                    {ligne.nom_item}
                  </TableCell>
                  <TableCell>
                    {ligne.prestataire_nom ? (
                      <>
                        <Person fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        {ligne.prestataire_nom}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        — (revente)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{ligne.quantite}</TableCell>
                  <TableCell align="right">{htg(ligne.total)}</TableCell>
                  <TableCell align="right">
                    {Number(ligne.montant_commission) > 0
                      ? <Typography variant="body2" color="success.main">
                          {ligne.pourcentage} % · {htg(ligne.montant_commission)}
                        </Typography>
                      : <Typography variant="body2" color="text.disabled">—</Typography>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="space-between">
            <Typography color="text.secondary">Sous-total</Typography>
            <Typography>{htg(detail?.sous_total)}</Typography>
          </Box>
          {Number(detail?.remise) > 0 && (
            <Box display="flex" justifyContent="space-between">
              <Typography color="text.secondary">Remise</Typography>
              <Typography color="error.main">- {htg(detail?.remise)}</Typography>
            </Box>
          )}
          <Box display="flex" justifyContent="space-between" mt={1}>
            <Typography fontWeight="bold">Total</Typography>
            <Typography fontWeight="bold">{htg(detail?.montant_total)}</Typography>
          </Box>
          {Number(detail?.total_commissions) > 0 && (
            <Box display="flex" justifyContent="space-between">
              <Typography color="text.secondary">Part des prestataires</Typography>
              <Typography color="success.main">{htg(detail?.total_commissions)}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {detail?.statut !== 'annule' && (
            <Button color="error" onClick={() => annuler(detail)}>Annuler le ticket</Button>
          )}
          <Button onClick={() => setDetail(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tickets;
